'use client';
import React, { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import Header from '@/components/Header';
import ViralScoreMeter from '@/app/home/components/ViralScoreMeter';

interface PreviewData {
  id: string;
  topic: string;
  platform: string;
  hook: string;
  script: string;
  caption: string;
  hashtags: string[];
  viral_score: number;
  posting_time: string;
  video_url: string;
  status: string;
  score_breakdown: {
    hook_strength: number;
    retention: number;
    emotion: number;
    trend_alignment: number;
  };
}

const PLATFORM_ICONS: Record<string, string> = {
  tiktok: '🎵',
  instagram: '📸',
  youtube: '▶️',
  twitter: '𝕏',
};

const PLATFORM_COLORS: Record<string, string> = {
  tiktok: '#C2185B',
  instagram: '#ff7043',
  youtube: '#ffa726',
  twitter: '#60a5fa',
};

const MOCK_PREVIEW: PreviewData = {
  id: 'prev-001',
  topic: 'fat loss hack 2026',
  platform: 'tiktok',
  hook: "You're losing fat the wrong way — and science just proved it.",
  script: `[0–3s] Hook: "You're losing fat the wrong way — and science just proved it."

[3–8s] Problem: Most people cut calories and do cardio. But new research shows this actually SLOWS your metabolism by 23%.

[8–18s] Solution: The real fat loss formula is: Protein timing + resistance training + sleep optimization. Not starvation.

[18–25s] Proof: Studies from 2025 show people who ate MORE protein lost 2x the fat in 8 weeks vs calorie-cutters.

[25–30s] CTA: Follow for the exact protocol. I'll break it down in the next video.`,
  caption: `Most people are dieting WRONG 😤

New science shows cutting calories actually slows your metabolism by 23%.

The real formula? Protein timing + resistance + sleep.

Drop a 🔥 if you want the full protocol 👇

#fatlosshack #weightloss2026 #metabolismboost #fitnesstips #viralhealth`,
  hashtags: ['#fatlosshack', '#weightloss2026', '#metabolismboost', '#fitnesstips', '#viralhealth'],
  viral_score: 87,
  posting_time: '7:00 PM EST',
  video_url: '',
  status: 'ready',
  score_breakdown: {
    hook_strength: 92,
    retention: 84,
    emotion: 88,
    trend_alignment: 79,
  },
};

function ScoreBar({ label, value, color }: { label: string; value: number; color: string }) {
  const [animated, setAnimated] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      let current = 0;
      const step = Math.ceil(value / 30);
      const interval = setInterval(() => {
        current = Math.min(current + step, value);
        setAnimated(current);
        if (current >= value) clearInterval(interval);
      }, 25);
      return () => clearInterval(interval);
    }, 300);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium" style={{ color: 'var(--chrome-dim)' }}>{label}</span>
        <span className="text-xs font-bold font-mono" style={{ color }}>{animated}</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${animated}%`,
            background: `linear-gradient(90deg, ${color}80, ${color})`,
            boxShadow: `0 0 8px ${color}60`,
            transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        />
      </div>
    </div>
  );
}

function SocialPreviewCard({ data }: { data: PreviewData }) {
  const platform = data.platform.toLowerCase();
  const icon = PLATFORM_ICONS[platform] ?? '📱';
  const color = PLATFORM_COLORS[platform] ?? 'var(--ruby)';
  const lines = data.caption.split('\n').filter(Boolean);

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      {/* Platform bar */}
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ background: `${color}18`, borderBottom: `1px solid ${color}25` }}
      >
        <span className="text-base">{icon}</span>
        <span className="text-xs font-semibold capitalize" style={{ color }}>{data.platform} Preview</span>
        <div className="ml-auto flex gap-1">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="w-2 h-2 rounded-full" style={{ background: i === 0 ? '#ff5f57' : i === 1 ? '#febc2e' : '#28c840' }} />
          ))}
        </div>
      </div>

      {/* Post mockup */}
      <div className="p-4 space-y-3">
        {/* User row */}
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ background: `linear-gradient(135deg, var(--ruby), var(--ruby-dark))`, color: 'white' }}
          >
            V
          </div>
          <div>
            <p className="text-xs font-semibold" style={{ color: 'var(--chrome)' }}>@viralforge</p>
            <p className="text-xs" style={{ color: 'var(--chrome-dim)' }}>Just now · {icon}</p>
          </div>
          <div
            className="ml-auto px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}
          >
            Follow
          </div>
        </div>

        {/* Video thumbnail */}
        <div
          className="w-full rounded-xl flex items-center justify-center relative overflow-hidden"
          style={{ height: '160px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}
        >
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(135deg, rgba(194,24,91,0.15) 0%, rgba(0,0,0,0.6) 100%)'
          }} />
          <div className="relative flex flex-col items-center gap-2">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}
            >
              <svg className="w-5 h-5 ml-0.5" fill="white" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
            <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>AI Video · 30s</span>
          </div>
          {/* Viral score badge */}
          <div
            className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold"
            style={{ background: 'rgba(194,24,91,0.85)', color: 'white', backdropFilter: 'blur(4px)' }}
          >
            🔥 {data.viral_score}
          </div>
        </div>

        {/* Caption preview */}
        <div className="space-y-1">
          {lines.slice(0, 3).map((line, i) => (
            <p key={i} className="text-xs leading-relaxed" style={{ color: i === 0 ? 'var(--chrome)' : 'var(--chrome-dim)' }}>
              {line}
            </p>
          ))}
          {lines.length > 3 && (
            <p className="text-xs" style={{ color: 'var(--ruby)' }}>...more</p>
          )}
        </div>

        {/* Engagement row */}
        <div className="flex items-center gap-4 pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          {[['❤️', '24.3K'], ['💬', '1.2K'], ['🔁', '8.7K']].map(([icon, count]) => (
            <div key={icon} className="flex items-center gap-1">
              <span className="text-xs">{icon}</span>
              <span className="text-xs font-mono" style={{ color: 'var(--chrome-dim)' }}>{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreviewContent() {
  const searchParams = useSearchParams();
  const [data] = useState<PreviewData>(MOCK_PREVIEW);
  const [activeTab, setActiveTab] = useState<'script' | 'caption'>('script');
  const [autoPost, setAutoPost] = useState(false);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const [copied, setCopied] = useState(false);

  const platform = data.platform.toLowerCase();
  const platformColor = PLATFORM_COLORS[platform] ?? 'var(--ruby)';

  const handlePost = async () => {
    setPosting(true);
    await new Promise(r => setTimeout(r, 2000));
    setPosting(false);
    setPosted(true);
  };

  const handleCopy = () => {
    const text = `Hook: ${data.hook}\n\nScript:\n${data.script}\n\nCaption:\n${data.caption}\n\n${data.hashtags.join(' ')}`;
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--blackout)' }}>
      <Header />

      <div className="max-w-7xl mx-auto px-6 pt-28 pb-16">
        {/* Page header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link
                href="/"
                className="text-xs font-medium flex items-center gap-1.5 transition-colors"
                style={{ color: 'var(--chrome-dim)' }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </Link>
              <span style={{ color: 'rgba(255,255,255,0.15)' }}>·</span>
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                style={{ background: 'rgba(194,24,91,0.12)', color: 'var(--ruby)', border: '1px solid rgba(194,24,91,0.25)' }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                Content Preview
              </div>
            </div>
            <h1 className="text-2xl font-bold font-display" style={{ color: 'var(--chrome)' }}>
              Review Before Posting
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--chrome-dim)' }}>
              Topic: <span style={{ color: 'var(--chrome)' }}>{data.topic}</span>
              <span className="mx-2" style={{ color: 'rgba(255,255,255,0.15)' }}>·</span>
              {PLATFORM_ICONS[platform]} <span className="capitalize">{data.platform}</span>
            </p>
          </div>

          {/* Post actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleCopy}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--chrome-dim)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              {copied ? '✓ Copied' : 'Copy All'}
            </button>
            {!posted ? (
              <button
                onClick={handlePost}
                disabled={posting}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2"
                style={{
                  background: posting ? 'rgba(194,24,91,0.3)' : 'linear-gradient(135deg, var(--ruby) 0%, var(--ruby-dark) 100%)',
                  color: 'white',
                  border: '1px solid rgba(194,24,91,0.4)',
                  cursor: posting ? 'not-allowed' : 'pointer',
                }}
              >
                {posting ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3" />
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    Posting...
                  </>
                ) : (
                  <>📤 Post Now</>
                )}
              </button>
            ) : (
              <div
                className="px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2"
                style={{ background: 'rgba(74,222,128,0.12)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.25)' }}
              >
                ✓ Posted Successfully
              </div>
            )}
          </div>
        </div>

        {/* Main grid */}
        <div className="grid lg:grid-cols-12 gap-5">

          {/* LEFT COLUMN: Video + Score */}
          <div className="lg:col-span-4 space-y-5">

            {/* Video Player */}
            <div
              className="rounded-2xl overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                <span className="text-sm font-semibold" style={{ color: 'var(--chrome)' }}>Generated Video</span>
                <div
                  className="px-2.5 py-1 rounded-full text-xs font-semibold"
                  style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.2)' }}
                >
                  AI Generated
                </div>
              </div>
              <div
                className="mx-5 mb-5 rounded-xl flex items-center justify-center relative overflow-hidden"
                style={{ height: '240px', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div className="absolute inset-0" style={{
                  background: 'linear-gradient(135deg, rgba(194,24,91,0.2) 0%, rgba(0,0,0,0.7) 60%, rgba(194,24,91,0.1) 100%)'
                }} />
                {/* Animated particles */}
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full animate-pulse"
                    style={{
                      width: `${4 + i * 2}px`,
                      height: `${4 + i * 2}px`,
                      background: `rgba(194,24,91,${0.3 + i * 0.05})`,
                      top: `${15 + i * 12}%`,
                      left: `${10 + i * 14}%`,
                      animationDelay: `${i * 0.3}s`,
                    }}
                  />
                ))}
                <div className="relative flex flex-col items-center gap-3">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)' }}
                  >
                    <svg className="w-7 h-7 ml-1" fill="white" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold" style={{ color: 'var(--chrome)' }}>AI Video Ready</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--chrome-dim)' }}>30 seconds · 9:16 vertical</p>
                  </div>
                </div>
                {/* Duration badge */}
                <div
                  className="absolute bottom-3 right-3 px-2 py-0.5 rounded text-xs font-mono font-bold"
                  style={{ background: 'rgba(0,0,0,0.7)', color: 'white' }}
                >
                  0:30
                </div>
              </div>
            </div>

            {/* Viral Score Breakdown */}
            <div
              className="rounded-2xl p-5"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="flex items-center justify-between mb-5">
                <span className="text-sm font-semibold" style={{ color: 'var(--chrome)' }}>Viral Score Breakdown</span>
                <ViralScoreMeter score={data.viral_score} size="md" />
              </div>
              <div className="space-y-4">
                <ScoreBar label="Hook Strength (0–3s)" value={data.score_breakdown.hook_strength} color="#C2185B" />
                <ScoreBar label="Retention Curve" value={data.score_breakdown.retention} color="#ff7043" />
                <ScoreBar label="Emotional Impact" value={data.score_breakdown.emotion} color="#fbbf24" />
                <ScoreBar label="Trend Alignment" value={data.score_breakdown.trend_alignment} color="#60a5fa" />
              </div>
              <div
                className="mt-4 p-3 rounded-xl flex items-center gap-2"
                style={{ background: 'rgba(194,24,91,0.06)', border: '1px solid rgba(194,24,91,0.15)' }}
              >
                <span className="text-base">⏰</span>
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--chrome)' }}>Best Posting Time</p>
                  <p className="text-xs" style={{ color: 'var(--ruby)' }}>{data.posting_time}</p>
                </div>
              </div>
            </div>
          </div>

          {/* CENTER COLUMN: Hook + Script/Caption */}
          <div className="lg:col-span-5 space-y-5">

            {/* Hook */}
            <div
              className="rounded-2xl p-5"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(194,24,91,0.2)' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-base">🎣</span>
                <span className="text-sm font-semibold" style={{ color: 'var(--chrome)' }}>Hook</span>
                <span className="text-xs ml-auto" style={{ color: 'var(--chrome-dim)' }}>0–3 seconds</span>
              </div>
              <p
                className="text-base font-medium leading-relaxed p-4 rounded-xl"
                style={{ background: 'rgba(194,24,91,0.08)', color: 'var(--chrome)', border: '1px solid rgba(194,24,91,0.15)', fontStyle: 'italic' }}
              >
                "{data.hook}"
              </p>
              <div className="mt-3 flex items-center gap-2">
                <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-full rounded-full" style={{ width: `${data.score_breakdown.hook_strength}%`, background: 'linear-gradient(90deg, rgba(194,24,91,0.5), var(--ruby))' }} />
                </div>
                <span className="text-xs font-mono" style={{ color: 'var(--ruby)' }}>{data.score_breakdown.hook_strength}/100</span>
              </div>
            </div>

            {/* Script / Caption tabs */}
            <div
              className="rounded-2xl p-5"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
                {(['script', 'caption'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className="flex-1 py-2 rounded-lg text-xs font-semibold capitalize transition-all"
                    style={{
                      background: activeTab === tab ? 'rgba(194,24,91,0.2)' : 'transparent',
                      color: activeTab === tab ? 'var(--ruby)' : 'var(--chrome-dim)',
                    }}
                  >
                    {tab === 'script' ? '📝 Script' : '✍️ Caption'}
                  </button>
                ))}
              </div>
              <div
                className="text-sm leading-relaxed p-4 rounded-xl whitespace-pre-line"
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  color: 'var(--chrome)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  minHeight: '200px',
                  fontFamily: activeTab === 'script' ? 'var(--font-mono, monospace)' : 'inherit',
                  fontSize: activeTab === 'script' ? '12px' : '13px',
                }}
              >
                {activeTab === 'script' ? data.script : data.caption}
              </div>
            </div>

            {/* Hashtags */}
            <div
              className="rounded-2xl p-5"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <span className="text-sm font-semibold block mb-3" style={{ color: 'var(--chrome)' }}># Hashtags</span>
              <div className="flex flex-wrap gap-2">
                {data.hashtags.map((tag, i) => (
                  <span
                    key={i}
                    className="text-xs px-3 py-1.5 rounded-lg font-mono transition-all cursor-pointer"
                    style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--chrome-dim)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Social Preview + Auto-post */}
          <div className="lg:col-span-3 space-y-5">

            {/* Social Preview */}
            <SocialPreviewCard data={data} />

            {/* Auto-post settings */}
            <div
              className="rounded-2xl p-5 space-y-4"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <span className="text-sm font-semibold block" style={{ color: 'var(--chrome)' }}>Post Settings</span>

              {/* Auto-post toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--chrome)' }}>Auto-Post</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--chrome-dim)' }}>Schedule at best time</p>
                </div>
                <button
                  onClick={() => setAutoPost(!autoPost)}
                  className="relative w-10 h-5 rounded-full transition-all flex-shrink-0"
                  style={{ background: autoPost ? 'var(--ruby)' : 'rgba(255,255,255,0.1)' }}
                >
                  <span
                    className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
                    style={{
                      background: 'white',
                      left: autoPost ? '20px' : '2px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    }}
                  />
                </button>
              </div>

              {autoPost && (
                <div
                  className="p-3 rounded-xl text-xs"
                  style={{ background: 'rgba(194,24,91,0.08)', color: 'var(--chrome-dim)', border: '1px solid rgba(194,24,91,0.15)' }}
                >
                  📅 Will post at <span style={{ color: 'var(--ruby)', fontWeight: 600 }}>{data.posting_time}</span> for maximum reach
                </div>
              )}

              {/* Platform info */}
              <div
                className="p-3 rounded-xl flex items-center gap-2.5"
                style={{ background: `${platformColor}10`, border: `1px solid ${platformColor}20` }}
              >
                <span className="text-lg">{PLATFORM_ICONS[platform]}</span>
                <div>
                  <p className="text-xs font-semibold capitalize" style={{ color: 'var(--chrome)' }}>{data.platform}</p>
                  <p className="text-xs" style={{ color: 'var(--chrome-dim)' }}>Optimized format</p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-2 pt-1">
                {!posted ? (
                  <button
                    onClick={handlePost}
                    disabled={posting}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all"
                    style={{
                      background: posting ? 'rgba(194,24,91,0.3)' : 'linear-gradient(135deg, var(--ruby) 0%, var(--ruby-dark) 100%)',
                      color: 'white',
                      border: '1px solid rgba(194,24,91,0.4)',
                      cursor: posting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {posting ? 'Posting...' : autoPost ? '📅 Schedule Post' : '📤 Post Now'}
                  </button>
                ) : (
                  <div
                    className="w-full py-2.5 rounded-xl text-sm font-semibold text-center"
                    style={{ background: 'rgba(74,222,128,0.12)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.25)' }}
                  >
                    ✓ Posted!
                  </div>
                )}
                <Link
                  href="/dashboard"
                  className="block w-full py-2.5 rounded-xl text-sm font-semibold text-center transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--chrome-dim)', border: '1px solid rgba(255,255,255,0.07)' }}
                >
                  View Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PreviewPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--blackout)' }}>
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin w-8 h-8" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="rgba(194,24,91,0.3)" strokeWidth="3" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="var(--ruby)" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <p className="text-sm" style={{ color: 'var(--chrome-dim)' }}>Loading preview...</p>
        </div>
      </div>
    }>
      <PreviewContent />
    </Suspense>
  );
}
